import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ClipboardList, RefreshCw, BarChart3, Eye, FileSpreadsheet } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';
import * as XLSX from 'xlsx';
import { SubstituteFilledBadge } from '@/components/guru-pengganti/FilledByCell';
import { formatWIB } from '@/lib/datetime';
import KehadiranPerkiraanNotice from '@/components/journal/KehadiranPerkiraanNotice';

export default function WaliKelasJurnalKelasPage() {
  const { user } = useAuth();
  const myClassId = user?.homeroom_class_id;
  const myClassName = user?.homeroom_class_name;

  const [data, setData] = useState({ items: [], total: 0, summary: {} });
  const [statsByTeacher, setStatsByTeacher] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filters, setFilters] = useState({
    teacher_id: 'all', subject_id: 'all',
    start_date: '', end_date: '',
  });
  const [detailDialog, setDetailDialog] = useState({ open: false, journal: null });
  const [attendanceDetail, setAttendanceDetail] = useState(null);
  const [loadingAttendance, setLoadingAttendance] = useState(false);

  const buildParams = () => {
    const p = { class_id: myClassId }; // Always filter by wali kelas's class
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== 'all') p[k] = v;
    });
    return p;
  };

  const load = async () => {
    if (!myClassId) {
      toast.error('Anda belum ditugaskan sebagai wali kelas');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get('/admin/jurnal', { params: buildParams() }),
        api.get('/admin/jurnal/stats-by-teacher', { params: { class_id: myClassId } }),
      ]);
      setData(r.data);
      setStatsByTeacher(s.data);
    } catch (e) {
      toast.error('Gagal memuat data jurnal');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const [sub, u] = await Promise.all([
        api.get('/subjects'),
        api.get('/users'),
      ]);
      setSubjects(sub.data);
      setTeachers(u.data.filter((x) => x.roles?.some((rr) => ['guru', 'wali_kelas', 'guru_piket', 'guru_bk', 'guru_tata_tertib', 'guru_ekstrakurikuler'].includes(rr))));
      await load();
    })();
    // eslint-disable-next-line
  }, []);

  const loadAttendanceDetail = async (journalId) => {
    try {
      setLoadingAttendance(true);
      console.log('[WALIKELAS-ATTENDANCE] Loading attendance for journal:', journalId);
      const res = await api.get(`/journals/${journalId}/attendance`);
      console.log('[WALIKELAS-ATTENDANCE] Response data:', res.data);
      console.log('[WALIKELAS-ATTENDANCE] attendance object:', res.data?.attendance);
      console.log('[WALIKELAS-ATTENDANCE] summary:', res.data?.summary);
      setAttendanceDetail(res.data);
      setLoadingAttendance(false);
    } catch (err) {
      console.error('[WALIKELAS-ATTENDANCE] Error loading attendance detail:', err);
      console.error('[WALIKELAS-ATTENDANCE] Error response:', err.response?.data);
      setLoadingAttendance(false);
      toast.error('Gagal memuat detail kehadiran');
    }
  };

  const handleShowDetail = (journal) => {
    setDetailDialog({ open: true, journal });
    setAttendanceDetail(null);
    loadAttendanceDetail(journal.id);
  };

  const reset = () => {
    setFilters({ teacher_id: 'all', subject_id: 'all', start_date: '', end_date: '' });
    setTimeout(() => load(), 50);
  };

  // Detail kehadiran per status, dikelompokkan dari attendance_details -> teks
  // "Hadir: A, B | Sakit: C | Izin: - | Alpa: D" untuk satu sel kolom export.
  const formatAttendanceDetail = (j) => {
    const details = j.attendance_details || [];
    if (details.length === 0) return '-';
    const byStatus = { hadir: [], sakit: [], izin: [], alpa: [] };
    details.forEach((d) => {
      const status = d.status === 'alpha' ? 'alpa' : d.status;
      if (byStatus[status]) byStatus[status].push(d.student_name);
    });
    const labels = { hadir: 'Hadir', sakit: 'Sakit', izin: 'Izin', alpa: 'Alpa' };
    return Object.entries(byStatus)
      .map(([status, names]) => `${labels[status]}: ${names.length > 0 ? names.join(', ') : '-'}`)
      .join(' | ');
  };

  const exportExcel = () => {
    if (!data.items.length) return;

    const excelData = data.items.map((j) => ({
      'Tanggal': formatWIB(j.started_at, ''),
      'JTM': j.jtm_count || 1,
      'Mapel': j.subject_name || '-',
      'Guru': j.teacher_name || '-',
      'Ruang': j.room_name || '-',
      'KD/Indikator': j.kd_indikator || '-',
      'Materi/Pokok Bahasan (Terdaftar)': j.materi_nama || '-',
      'Materi': j.materi || '-',
      'Catatan': j.catatan || '-',
      'Hadir': j.siswa_hadir || 0,
      'Sakit': j.siswa_sakit || 0,
      'Izin': j.siswa_izin || 0,
      'Alpa': j.siswa_tidak_hadir || 0,
      'Detail Kehadiran Siswa': formatAttendanceDetail(j),
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Jurnal ${myClassName}`);

    const colWidths = [
      { wch: 20 }, // Tanggal
      { wch: 5 },  // JTM
      { wch: 25 }, // Mapel
      { wch: 25 }, // Guru
      { wch: 15 }, // Ruang
      { wch: 25 }, // KD/Indikator
      { wch: 30 }, // Materi/Pokok Bahasan (Terdaftar)
      { wch: 40 }, // Materi
      { wch: 40 }, // Catatan
      { wch: 8 },  // Hadir
      { wch: 8 },  // Sakit
      { wch: 8 },  // Izin
      { wch: 8 },  // Alpa
      { wch: 60 }, // Detail Kehadiran Siswa
    ];
    ws['!cols'] = colWidths;

    XLSX.writeFile(wb, `jurnal-kelas-${myClassName}-${Date.now()}.xlsx`);
    toast.success('File Excel berhasil diunduh');
  };

  const exportCSV = () => {
    if (!data.items.length) return;
    const headers = ['Tanggal', 'JTM', 'Mapel', 'Guru', 'Ruang', 'KD/Indikator', 'Materi/Pokok Bahasan (Terdaftar)', 'Materi', 'Catatan', 'Hadir', 'Sakit', 'Izin', 'Alpa', 'Detail Kehadiran Siswa'];
    const rows = data.items.map((j) => [
      formatWIB(j.started_at, ''), j.jtm_count || 1, j.subject_name, j.teacher_name, j.room_name,
      JSON.stringify(j.kd_indikator || ''), JSON.stringify(j.materi_nama || ''), JSON.stringify(j.materi), JSON.stringify(j.catatan || ''), j.siswa_hadir, j.siswa_sakit, j.siswa_izin, j.siswa_tidak_hadir,
      JSON.stringify(formatAttendanceDetail(j)),
    ]);
    const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `jurnal-kelas-${myClassName}-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const openDetail = async (journal) => {
    try {
      const { data: fullJournal } = await api.get(`/jurnal/${journal.id}`);
      handleShowDetail(fullJournal);
    } catch (e) {
      toast.error('Gagal memuat detail jurnal');
    }
  };

  if (!myClassId) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <Card>
          <CardContent className="py-12 text-center">
            <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <div className="text-slate-700 font-semibold">Anda belum ditugaskan sebagai wali kelas</div>
            <div className="text-sm text-slate-500 mt-1">
              Silakan hubungi admin untuk penugasan wali kelas
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Badge className="bg-[#006837]/10 text-[#006837] border-[#006837]/20 mb-2">
            <ClipboardList className="h-3 w-3 mr-1" /> Jurnal Kelas
          </Badge>
          <h1 className="text-3xl font-bold text-slate-900">Jurnal Kelas {myClassName}</h1>
          <p className="text-sm text-slate-600 mt-1">Jurnal mengajar dari semua guru di kelas Anda</p>
        </div>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Guru</Label>
              <Select value={filters.teacher_id} onValueChange={(v) => setFilters({ ...filters, teacher_id: v })}>
                <SelectTrigger><SelectValue placeholder="Semua Guru" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Guru</SelectItem>
                  {teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Mapel</Label>
              <Select value={filters.subject_id} onValueChange={(v) => setFilters({ ...filters, subject_id: v })}>
                <SelectTrigger><SelectValue placeholder="Semua Mapel" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Mapel</SelectItem>
                  {subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Dari Tanggal</Label><Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} /></div>
            <div><Label className="text-xs">Sampai Tanggal</Label><Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={reset}>Reset</Button>
            <Button onClick={load} className="bg-[#006837] hover:bg-[#0B7A3B] gap-1" size="sm"><RefreshCw className="h-3.5 w-3.5" /> Terapkan Filter</Button>
            <Button onClick={exportExcel} variant="outline" size="sm" disabled={!data.items.length} className="gap-1"><FileSpreadsheet className="h-3.5 w-3.5" /> Excel</Button>
            <Button onClick={exportCSV} variant="outline" size="sm" disabled={!data.items.length}>CSV</Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <SummaryCard label="Total Jurnal" value={data.total} color="slate" />
        <SummaryCard label="Total Hadir" value={data.summary?.total_hadir || 0} color="emerald" />
        <SummaryCard label="Total Sakit" value={data.summary?.total_sakit || 0} color="amber" />
        <SummaryCard label="Total Izin" value={data.summary?.total_izin || 0} color="blue" />
        <SummaryCard label="Total Alpa" value={data.summary?.total_alpa || 0} color="rose" />
      </div>

      <Tabs defaultValue="items">
        <TabsList>
          <TabsTrigger value="items">Daftar Jurnal ({data.total})</TabsTrigger>
          <TabsTrigger value="by-teacher"><BarChart3 className="h-4 w-4 mr-1" /> Per Guru</TabsTrigger>
        </TabsList>

        <TabsContent value="items">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tanggal</TableHead>
                      <TableHead>JTM</TableHead>
                      <TableHead>Mapel</TableHead>
                      <TableHead>Guru</TableHead>
                      <TableHead>Diisi Oleh</TableHead>
                      <TableHead>Ruang</TableHead>
                      <TableHead>Materi</TableHead>
                      <TableHead className="text-center">Hadir</TableHead>
                      <TableHead className="text-center">Sakit</TableHead>
                      <TableHead className="text-center">Izin</TableHead>
                      <TableHead className="text-center">Alpa</TableHead>
                      <TableHead className="text-center">Aksi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow><TableCell colSpan={12} className="text-center py-8 text-slate-500">Memuat data...</TableCell></TableRow>
                    ) : data.items.length === 0 ? (
                      <TableRow><TableCell colSpan={12} className="text-center py-8 text-slate-500">Belum ada jurnal untuk kelas ini</TableCell></TableRow>
                    ) : (
                      data.items.map((j) => (
                        <TableRow key={j.id}>
                          <TableCell className="text-xs">{formatWIB(j.started_at, '', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</TableCell>
                          <TableCell className="text-center">{j.jtm_count || 1}</TableCell>
                          <TableCell className="text-sm">{j.subject_name}</TableCell>
                          <TableCell className="text-sm">{j.teacher_name}</TableCell>
                          <TableCell>
                            {j.fill_mode === 'substitute' ? (
                              <SubstituteFilledBadge journal={j} />
                            ) : j.fill_mode === 'piket' ? (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                                ✋ Piket{j.filled_by_name ? `: ${j.filled_by_name}` : ''}
                              </Badge>
                            ) : j.fill_mode === 'admin' ? (
                              <Badge className="bg-purple-100 text-purple-700 border-purple-200">Admin</Badge>
                            ) : (
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Pengajar</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">{j.room_name}</TableCell>
                          <TableCell className="max-w-xs truncate text-sm">{j.materi}</TableCell>
                          <TableCell className="text-center"><Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">{j.siswa_hadir || 0}</Badge></TableCell>
                          <TableCell className="text-center"><Badge className="bg-amber-50 text-amber-700 border-amber-200">{j.siswa_sakit || 0}</Badge></TableCell>
                          <TableCell className="text-center"><Badge className="bg-blue-50 text-blue-700 border-blue-200">{j.siswa_izin || 0}</Badge></TableCell>
                          <TableCell className="text-center"><Badge className="bg-rose-50 text-rose-700 border-rose-200">{j.siswa_tidak_hadir || 0}</Badge></TableCell>
                          <TableCell className="text-center">
                            <Button variant="ghost" size="sm" onClick={() => openDetail(j)}><Eye className="h-3 w-3" /></Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="by-teacher">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Guru</TableHead>
                      <TableHead className="text-center">Total Jurnal</TableHead>
                      <TableHead className="text-center">Total JTM</TableHead>
                      <TableHead className="text-center">Total Hadir</TableHead>
                      <TableHead className="text-center">Total Sakit</TableHead>
                      <TableHead className="text-center">Total Izin</TableHead>
                      <TableHead className="text-center">Total Alpa</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {statsByTeacher.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Belum ada data</TableCell></TableRow>
                    ) : (
                      statsByTeacher.map((s, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{s.teacher_name}</TableCell>
                          <TableCell className="text-center">{s.total_jurnal}</TableCell>
                          <TableCell className="text-center">{s.total_jtm}</TableCell>
                          <TableCell className="text-center"><Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">{s.total_hadir}</Badge></TableCell>
                          <TableCell className="text-center"><Badge className="bg-amber-50 text-amber-700 border-amber-200">{s.total_sakit}</Badge></TableCell>
                          <TableCell className="text-center"><Badge className="bg-blue-50 text-blue-700 border-blue-200">{s.total_izin}</Badge></TableCell>
                          <TableCell className="text-center"><Badge className="bg-rose-50 text-rose-700 border-rose-200">{s.total_alpa}</Badge></TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => setDetailDialog({ ...detailDialog, open })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detail Jurnal Mengajar</DialogTitle>
          </DialogHeader>
          {detailDialog.journal && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs text-slate-500">Tanggal & Waktu</Label><div className="font-medium">{formatWIB(detailDialog.journal.started_at, '')}</div></div>
                <div><Label className="text-xs text-slate-500">JTM</Label><div className="font-medium">{detailDialog.journal.jtm_count || 1}</div></div>
                <div><Label className="text-xs text-slate-500">Kelas</Label><div className="font-medium">{detailDialog.journal.class_name}</div></div>
                <div><Label className="text-xs text-slate-500">Mapel</Label><div className="font-medium">{detailDialog.journal.subject_name}</div></div>
                <div><Label className="text-xs text-slate-500">Guru</Label><div className="font-medium">{detailDialog.journal.teacher_name}</div></div>
                <div><Label className="text-xs text-slate-500">Ruang</Label><div className="font-medium">{detailDialog.journal.room_name}</div></div>
              </div>
              <div><Label className="text-xs text-slate-500">Materi</Label><div className="font-medium whitespace-pre-wrap">{detailDialog.journal.materi}</div></div>
              {detailDialog.journal.catatan && <div><Label className="text-xs text-slate-500">Catatan</Label><div className="font-medium whitespace-pre-wrap">{detailDialog.journal.catatan}</div></div>}

              {/* Summary */}
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center p-3 bg-emerald-50 rounded-lg border border-emerald-200"><div className="text-2xl font-bold text-emerald-700">{detailDialog.journal.siswa_hadir || 0}</div><div className="text-xs text-emerald-600">Hadir</div></div>
                <div className="text-center p-3 bg-amber-50 rounded-lg border border-amber-200"><div className="text-2xl font-bold text-amber-700">{detailDialog.journal.siswa_sakit || 0}</div><div className="text-xs text-amber-600">Sakit</div></div>
                <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200"><div className="text-2xl font-bold text-blue-700">{detailDialog.journal.siswa_izin || 0}</div><div className="text-xs text-blue-600">Izin</div></div>
                <div className="text-center p-3 bg-rose-50 rounded-lg border border-rose-200"><div className="text-2xl font-bold text-rose-700">{detailDialog.journal.siswa_tidak_hadir || 0}</div><div className="text-xs text-rose-600">Alpa</div></div>
              </div>

              {/* Detail Per Siswa - Grouped by Status */}
              {loadingAttendance ? (
                <div className="text-center py-8 text-slate-500 text-sm">
                  Memuat detail kehadiran...
                </div>
              ) : attendanceDetail && (
                <div>
                  <Label className="text-sm font-semibold mb-3 block">Detail Kehadiran Siswa</Label>
                  <KehadiranPerkiraanNotice show={attendanceDetail.kehadiran_perkiraan} />
                  <div className="grid grid-cols-1 gap-3">
                    {/* Siswa Hadir */}
                    {attendanceDetail.attendance.hadir.length > 0 && (
                      <Card className="border-emerald-200">
                        <CardHeader className="bg-emerald-50 py-2 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                            <span className="text-sm font-semibold text-emerald-700">
                              Hadir ({attendanceDetail.attendance.hadir.length})
                            </span>
                          </div>
                        </CardHeader>
                        <CardContent className="p-3">
                          <div className="grid grid-cols-2 gap-2">
                            {attendanceDetail.attendance.hadir.map((student, idx) => (
                              <div key={idx} className="text-sm bg-emerald-50 p-2 rounded border border-emerald-200">
                                {student.student_name}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Siswa Sakit */}
                    {attendanceDetail.attendance.sakit.length > 0 && (
                      <Card className="border-amber-200">
                        <CardHeader className="bg-amber-50 py-2 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                            <span className="text-sm font-semibold text-amber-700">
                              Sakit ({attendanceDetail.attendance.sakit.length})
                            </span>
                          </div>
                        </CardHeader>
                        <CardContent className="p-3">
                          <div className="grid grid-cols-2 gap-2">
                            {attendanceDetail.attendance.sakit.map((student, idx) => (
                              <div key={idx} className="text-sm bg-amber-50 p-2 rounded border border-amber-200">
                                {student.student_name}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Siswa Izin */}
                    {attendanceDetail.attendance.izin.length > 0 && (
                      <Card className="border-blue-200">
                        <CardHeader className="bg-blue-50 py-2 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                            <span className="text-sm font-semibold text-blue-700">
                              Izin ({attendanceDetail.attendance.izin.length})
                            </span>
                          </div>
                        </CardHeader>
                        <CardContent className="p-3">
                          <div className="grid grid-cols-2 gap-2">
                            {attendanceDetail.attendance.izin.map((student, idx) => (
                              <div key={idx} className="text-sm bg-blue-50 p-2 rounded border border-blue-200">
                                {student.student_name}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Siswa Alpha */}
                    {attendanceDetail.attendance.alpha.length > 0 && (
                      <Card className="border-rose-200">
                        <CardHeader className="bg-rose-50 py-2 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-rose-500"></div>
                            <span className="text-sm font-semibold text-rose-700">
                              Alpha ({attendanceDetail.attendance.alpha.length})
                            </span>
                          </div>
                        </CardHeader>
                        <CardContent className="p-3">
                          <div className="grid grid-cols-2 gap-2">
                            {attendanceDetail.attendance.alpha.map((student, idx) => (
                              <div key={idx} className="text-sm bg-rose-50 p-2 rounded border border-rose-200">
                                {student.student_name}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  const colorClasses = {
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
  };
  return (
    <Card className={`border ${colorClasses[color]}`}>
      <CardContent className="p-3">
        <div className="text-xs opacity-75 mb-1">{label}</div>
        <div className="text-2xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}
